import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, MapPin, XCircle } from 'lucide-react';
import { supabase } from '../supabase';

// Campo de dirección de entrega: autocompletado de Google + verificación contra Cabify.
//
// ── Autocompletado ──
// Usa **Places API (New)** por REST, no el SDK de JavaScript. Dos razones:
//   1. El proyecto de Google tiene habilitada solo la API nueva; el widget clásico
//      `google.maps.places.Autocomplete` es de la API legacy y devuelve REQUEST_DENIED
//      ("You're calling a legacy API, which is not enabled for your project").
//   2. Sin el script de Maps el bundle no carga el SDK entero y el desplegable lo
//      dibujamos nosotros, con el mismo estilo que el resto del admin.
// La key va en el frontend a propósito: está restringida por dominio en Google Cloud
// (verificado 01/10: funciona desde admin.krilic.com.ar y se bloquea desde otro origen).
// `sessionToken` encadena las sugerencias con el detalle del lugar: Google lo factura
// como UNA sesión en vez de cobrar cada tecleo.
//
// ── Verificación ──
// El que decide si una dirección sirve es Cabify, y su geocoder es estricto: "Franklin
// D. Roosevelt 2891" cotiza pero falla al crear la etiqueta por no tener localidad
// (caso real 30/09). El botón Verificar cotiza con `estimate` (NO despacha ni cobra) y
// responde las dos cosas: si la dirección se entiende y si está en zona, con el precio.
// Cuando la dirección viene del autocompletado se cotiza con lat/lon y Cabify ni
// geocodifica.

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
const PLACES = 'https://places.googleapis.com/v1';

interface Suggestion { placeId: string; text: string }

export interface AddressCheck { ok: boolean; message: string; priceArs?: number }

export function AddressField({
    value, onChange, onCoords, onPlace, placeholder = 'Calle 123, Localidad', className, showVerify = true, inputStyle,
}: {
    value: string;
    onChange: (v: string) => void;
    onCoords?: (c: { lat: number; lon: number } | null) => void;
    /** Al elegir una sugerencia: componentes de dirección de Google (provincia, partido…),
     *  para deducir la zona de envío (src/shippingZones.ts). */
    onPlace?: (p: { formatted: string; components: { longText?: string; types?: string[] }[] }) => void;
    placeholder?: string;
    className?: string;
    /** El botón Verificar sobra donde el flujo YA cotiza con Cabify (ej: /cabify, que
     *  cotiza al apretar "Cotizar"). Ahí se oculta y queda solo el autocompletado. */
    showVerify?: boolean;
    /** Estilo del input, para que matchee los demás campos del formulario donde se usa. */
    inputStyle?: React.CSSProperties;
}) {
    const [checking, setChecking] = useState(false);
    const [result, setResult] = useState<AddressCheck | null>(null);
    const [sugs, setSugs] = useState<Suggestion[]>([]);
    const [openList, setOpenList] = useState(false);
    const [loadingSugs, setLoadingSugs] = useState(false);
    const [hasCoords, setHasCoords] = useState(false);
    const coordsRef = useRef<{ lat: number; lon: number } | null>(null);
    const sessionRef = useRef<string>(
        // crypto.randomUUID no existe en contextos no seguros viejos: fallback simple
        typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Math.abs(Date.now())),
    );
    const boxRef = useRef<HTMLDivElement>(null);
    const typedRef = useRef(false); // solo buscar si el usuario tipeó (no al abrir una orden ya cargada)

    const newSession = () => {
        sessionRef.current = typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID() : String(Math.abs(Date.now()));
    };

    const setCoords = (c: { lat: number; lon: number } | null) => {
        coordsRef.current = c;
        setHasCoords(!!c);
        onCoords?.(c);
    };

    // Cerrar el desplegable al clickear afuera
    useEffect(() => {
        if (!openList) return;
        const onDoc = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpenList(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [openList]);

    // Sugerencias con debounce: una request cada 350ms de quietud, no por tecla.
    useEffect(() => {
        if (!MAPS_KEY || !typedRef.current) return;
        const term = value.trim();
        if (term.length < 4) { setSugs([]); return; }
        const t = setTimeout(async () => {
            setLoadingSugs(true);
            try {
                const r = await fetch(`${PLACES}/places:autocomplete`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': MAPS_KEY },
                    body: JSON.stringify({ input: term, includedRegionCodes: ['ar'], sessionToken: sessionRef.current }),
                });
                const d = await r.json();
                const list: Suggestion[] = (d?.suggestions || [])
                    .map((s: any) => ({ placeId: s?.placePrediction?.placeId, text: s?.placePrediction?.text?.text }))
                    .filter((s: Suggestion) => s.placeId && s.text);
                setSugs(list);
                setOpenList(list.length > 0);
            } catch {
                setSugs([]); // sin sugerencias el campo sigue siendo texto libre
            } finally {
                setLoadingSugs(false);
            }
        }, 350);
        return () => clearTimeout(t);
    }, [value]);

    const choose = async (s: Suggestion) => {
        setOpenList(false);
        typedRef.current = false; // no re-buscar con el texto que acabamos de poner
        onChange(s.text);
        try {
            const r = await fetch(`${PLACES}/places/${s.placeId}?fields=formattedAddress,location,addressComponents&sessionToken=${sessionRef.current}`,
                { headers: { 'X-Goog-Api-Key': MAPS_KEY } });
            const d = await r.json();
            if (d?.formattedAddress) onChange(d.formattedAddress);
            const loc = d?.location;
            if (loc?.latitude != null && loc?.longitude != null) {
                setCoords({ lat: Number(loc.latitude), lon: Number(loc.longitude) });
            }
            onPlace?.({ formatted: d?.formattedAddress || s.text, components: d?.addressComponents || [] });
        } catch { /* queda la dirección de la sugerencia, sin coordenadas */ }
        newSession();
        setResult(null);
    };

    const check = async () => {
        const addr = value.trim();
        if (!addr) return;
        setChecking(true);
        setResult(null);
        try {
            const { data, error } = await supabase.functions.invoke('cabify-logistics', {
                body: {
                    action: 'estimate', dropoff_addr: addr,
                    ...(coordsRef.current ? { dropoff_lat: coordsRef.current.lat, dropoff_lon: coordsRef.current.lon } : {}),
                },
            });
            if (error) throw new Error(error.message);
            if (data?.ok) {
                const cents = Number(data?.estimation?.price?.amount || 0);
                setResult({
                    ok: true, priceArs: cents / 100,
                    message: `Entra en la zona de Cabify — envío ~$${Math.round(cents / 100).toLocaleString('es-AR')}`,
                });
            } else {
                setResult({ ok: false, message: String(data?.error || 'Cabify no pudo con esta dirección') });
            }
        } catch (e) {
            setResult({ ok: false, message: `No se pudo verificar: ${String((e as Error).message || e)}` });
        } finally {
            setChecking(false);
        }
    };

    return (
        // `tw`: tokens + reset acotado de uiarc → el campo se ve igual en el popup de Nueva venta
        // y en /cabify (antes, con estilos inline, dentro del popup quedaba sin borde).
        <div className={['tw relative', className].filter(Boolean).join(' ')} ref={boxRef}>
            <div className="flex items-stretch gap-2">
                <div className="relative min-w-0 flex-1">
                    <MapPin size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint" />
                    <input
                        type="text"
                        placeholder={placeholder}
                        value={value}
                        autoComplete="off"
                        onChange={e => {
                            typedRef.current = true;
                            onChange(e.target.value);
                            if (result) setResult(null);
                            // se editó a mano: las coordenadas viejas ya no corresponden
                            if (coordsRef.current) setCoords(null);
                        }}
                        onFocus={() => { if (sugs.length) setOpenList(true); }}
                        className="h-[var(--control-height-md)] w-full min-w-0 rounded-[var(--radius-control)] border border-[var(--border-strong)] bg-card pl-9 pr-3 text-[length:var(--text-sm)] text-ink placeholder:text-faint transition-colors hover:border-[var(--foreground)] focus:border-[var(--foreground)] focus:outline-none"
                        style={inputStyle}
                    />
                    {loadingSugs && <Loader2 size={14} className="spin absolute right-3 top-1/2 -translate-y-1/2 text-faint" />}
                </div>
                {showVerify && <button
                    type="button" onClick={check} disabled={checking || !value.trim()}
                    title="Cotizar con Cabify: dice si la dirección sirve y si está en zona (no despacha ni cobra)"
                    className="inline-flex h-[var(--control-height-md)] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[var(--radius-control)] border border-line bg-card px-3 text-[length:var(--text-sm)] font-medium text-ink transition-colors hover:bg-muted disabled:opacity-50">
                    {checking ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />} Verificar
                </button>}
            </div>

            {openList && sugs.length > 0 && (
                /* z-30: por encima de cabeceras sticky (20) y debajo de modales (50) */
                <div className="absolute left-0 right-0 z-30 mt-1 max-h-64 overflow-y-auto rounded-[var(--radius-panel)] border border-line bg-card p-1 shadow-[var(--shadow-floating)]">
                    {sugs.map(s => (
                        <button key={s.placeId} type="button" onClick={() => choose(s)}
                            className="flex w-full items-start gap-2 rounded-[calc(var(--radius-control)-2px)] px-2 py-2 text-left text-[length:var(--text-sm)] leading-snug text-ink hover:bg-muted">
                            <MapPin size={14} className="mt-0.5 shrink-0 text-faint" />
                            <span>{s.text}</span>
                        </button>
                    ))}
                </div>
            )}

            {MAPS_KEY && !result && (
                <div className={`mt-1.5 text-[length:var(--text-xs)] ${hasCoords ? 'text-[var(--success)]' : 'text-faint'}`}>
                    {hasCoords ? '✓ Ubicación exacta guardada' : 'Escribí calle y altura y elegí una sugerencia'}
                </div>
            )}

            {result && (
                <div className={`mt-1.5 flex items-start gap-1.5 text-[length:var(--text-xs)] leading-snug ${result.ok ? 'text-[var(--success)]' : 'text-[var(--warning)]'}`}>
                    {result.ok ? <CheckCircle2 size={14} className="mt-px shrink-0" /> : <XCircle size={14} className="mt-px shrink-0" />}
                    <span>{result.message}</span>
                </div>
            )}
        </div>
    );
}
